import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Save, Brain, FileText, HardDrive, Users, Trash2, Archive, Database,
  ChevronDown, ChevronUp, AlertTriangle,
  DollarSign, RotateCcw, BookMarked, Plus, X, GitCommit, Lock, Unlock, BookOpen,
  FolderTree, Loader,
} from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';
import ConsolidationHistory from '../consolidation/ConsolidationHistory';
import { createBook, getBook, updateBook } from '../../services/bookApi';
import { listGenres, getTerms as getDocTerms } from '../../utils/documentTerminology';
import { organizeHistory, organizeRollback } from '../../services/filesApi';

const ProjectSettings = ({ projectId, projectData, onProjectUpdate, onTabSwitch, user }) => {
  const [form, setForm] = useState({
    name: '', description: '', project_type: 'development', deadline: '', tags: ''
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [memory, setMemory] = useState(null);
  const [fileStats, setFileStats] = useState({ total: 0, size: '0 KB', types: {} });
  // Session 36 — quota stockage (authoritative backend)
  const [storageInfo, setStorageInfo] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [transMem, setTransMem] = useState(null);
  const [budget, setBudget] = useState(null);
  const [budgetForm, setBudgetForm] = useState({ budget_usd: 5, alert_threshold: 80, period_type: 'monthly' });
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetMsg, setBudgetMsg] = useState('');
  const [accessError, setAccessError] = useState(null);
  // Phase B2 — Bases d'expertise attachées
  const [expertiseAttachments, setExpertiseAttachments] = useState([]);
  const [accessibleBases, setAccessibleBases] = useState([]);
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [attachSelectedBaseId, setAttachSelectedBaseId] = useState('');
  const [attachMode, setAttachMode] = useState('hybrid');
  const [expertiseSaving, setExpertiseSaving] = useState(false);
  // S50 Phase 2.1 — Sessions hors mémoire (ephemeral)
  const [ephemeralSessions, setEphemeralSessions] = useState({ sessions: [], total: 0 });
  const [reactivatingId, setReactivatingId] = useState(null);
  // S50 Phase 3.4 — Consolidation auto settings
  const [consSettings, setConsSettings] = useState(null);
  const [consSettingsForm, setConsSettingsForm] = useState({
    auto_suggest: true, threshold_msgs: 10, threshold_idle_days: 7, threshold_cost_usd: 5.00
  });
  const [consSettingsSaving, setConsSettingsSaving] = useState(false);
  // S59 P3 — Historique organisations IA
  const [organizationsHistory, setOrganizationsHistory] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [rollbackInProgressId, setRollbackInProgressId] = useState(null);

  useEffect(() => {
    if (projectData) {
      setForm({
        name: projectData.name || '',
        description: projectData.description || '',
        project_type: projectData.project_type || projectData.type || 'development',
        deadline: projectData.deadline ? projectData.deadline.slice(0, 10) : '',
        tags: Array.isArray(projectData.tags) ? projectData.tags.join(', ') : (projectData.tags || '')
      });
    }
  }, [projectData]);

  useEffect(() => {
    if (projectId) {
      loadMemory();
      loadFileStats();
      loadTransMem();
      loadBudget();
      loadExpertiseAttachments();
      loadAccessibleBases();
      loadEphemeralSessions();
      loadConsSettings();
      loadOrganizationsHistory();
    }
  }, [projectId]);

  // S59 P3 — refresh historique organisations quand FileOrganizerModal applique/rollback
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail || e.detail.projectId === projectId) {
        loadOrganizationsHistory();
      }
    };
    window.addEventListener('file-organizer-changed', handler);
    return () => window.removeEventListener('file-organizer-changed', handler);
  }, [projectId]);

  const token = localStorage.getItem('oracle_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadMemory = async () => {
    try {
      setAccessError(null);
      const res = await axios.get(`/api/projects/${projectId}/memory`, { headers });
      setMemory(res.data);
    } catch (e) {
      if (e.response?.status === 403) setAccessError(e.response?.data?.message || 'Accès insuffisant à ce projet.');
      setMemory({ total_conversations: 0, themes: [], decisions: [], open_questions: [] });
    }
  };

  const loadEphemeralSessions = async () => {
    try {
      const res = await axios.get(`/api/projects/${projectId}/ephemeral-sessions`, { headers });
      setEphemeralSessions(res.data || { sessions: [], total: 0 });
    } catch (e) {
      setEphemeralSessions({ sessions: [], total: 0 });
    }
  };

  const loadConsSettings = async () => {
    try {
      const res = await axios.get(`/api/projects/${projectId}/consolidation-settings`, { headers });
      setConsSettings(res.data);
      setConsSettingsForm({
        auto_suggest: res.data.auto_suggest !== false,
        threshold_msgs: res.data.threshold_msgs || 10,
        threshold_idle_days: res.data.threshold_idle_days || 7,
        threshold_cost_usd: Number(res.data.threshold_cost_usd) || 5.00,
      });
    } catch (e) {
      if (e.response?.status === 403) setAccessError(e.response?.data?.message || 'Accès insuffisant à ce projet.');
      setConsSettings(null);
    }
  };

  const saveConsSettings = async () => {
    try {
      setConsSettingsSaving(true);
      const res = await axios.put(
        `/api/projects/${projectId}/consolidation-settings`,
        consSettingsForm,
        { headers }
      );
      setConsSettings(res.data);
      toast.success('Préférences de consolidation auto enregistrées.');
    } catch (e) {
      toast.error('Erreur sauvegarde : ' + (e.response?.data?.message || e.response?.data?.error || e.message));
    } finally {
      setConsSettingsSaving(false);
    }
  };

  // S59 P3 — Historique organisations IA + rollback 72h
  const loadOrganizationsHistory = async () => {
    if (!projectId) return;
    setLoadingOrganizations(true);
    try {
      const res = await organizeHistory(projectId, { limit: 10 });
      // Backend retourne { entries: [...] } ; on tolère aussi { history } ou un array direct
      const list = Array.isArray(res?.entries) ? res.entries
                 : Array.isArray(res?.history) ? res.history
                 : Array.isArray(res) ? res
                 : [];
      setOrganizationsHistory(list);
    } catch (e) {
      setOrganizationsHistory([]);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const handleOrganizationRollback = async (logId, stats) => {
    const ok = await confirmModal(
      `Annuler cette organisation ?\n\n${stats.files_moved} fichier(s) seront remis à leur emplacement d'origine. ` +
      `Les dossiers créés seront supprimés s'ils sont vides. Cette action est définitive.`,
      { danger: true, confirmText: 'Annuler l\'organisation', cancelText: 'Garder' }
    );
    if (!ok) return;
    setRollbackInProgressId(logId);
    try {
      const res = await organizeRollback(projectId, logId);
      toast.success(`Organisation annulée — ${res.files_restored || 0} fichier(s) restaurés.`);
      await loadOrganizationsHistory();
      // Notifie le parent pour refresh FilesSidebar + onglet Fichiers
      window.dispatchEvent(new CustomEvent('file-organizer-changed', { detail: { projectId, action: 'rollback' } }));
    } catch (e) {
      const msg = e.detail?.message || e.message || '';
      if (msg.includes('rollback_expired') || msg.includes('expired')) {
        toast.error('Fenêtre de rollback expirée (> 72h).');
      } else if (msg.includes('already_rolled_back')) {
        toast.error('Cette organisation a déjà été annulée.');
      } else {
        toast.error('Erreur rollback : ' + msg);
      }
    } finally {
      setRollbackInProgressId(null);
    }
  };

  const formatRelativeTime = (isoDate) => {
    if (!isoDate) return '—';
    const d = new Date(isoDate);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'à l\'instant';
    if (mins < 60) return `il y a ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `il y a ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `il y a ${days}j`;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatRollbackCountdown = (rollbackUntil, rolledBackAt) => {
    if (rolledBackAt) return 'Annulée';
    if (!rollbackUntil) return '—';
    const diffMs = new Date(rollbackUntil).getTime() - Date.now();
    if (diffMs <= 0) return 'Expirée';
    const hours = Math.floor(diffMs / 3600000);
    if (hours >= 24) return `Annulable encore ${Math.floor(hours / 24)}j ${hours % 24}h`;
    return `Annulable encore ${hours}h`;
  };

  const reactivateSession = async (sessionId) => {
    const session = ephemeralSessions.sessions.find(s => s.id === sessionId);
    const ok = await confirmModal(
      `Réactiver la mémoire de « ${session?.title || 'cette session'} » ?\n\nLes messages seront réindexés rétroactivement (hors messages supprimés).`,
      { danger: false, confirmLabel: 'Réactiver', cancelLabel: 'Annuler' }
    );
    if (!ok) return;
    try {
      setReactivatingId(sessionId);
      const res = await axios.put(
        `/api/projects/${projectId}/sessions/${sessionId}/memory-scope`,
        { scope: 'persistent' },
        { headers }
      );
      const affected = res.data?.messages_affected || 0;
      toast.success(`Mémoire réactivée — ${affected} message(s) réindexé(s).`);
      loadEphemeralSessions();
    } catch (e) {
      toast.error('Erreur réactivation : ' + (e.response?.data?.message || e.response?.data?.error || e.message));
    } finally {
      setReactivatingId(null);
    }
  };

  const loadTransMem = async () => {
    try {
      const res = await axios.get(`/api/projects/${projectId}/trans-memory`, { headers });
      setTransMem(res.data);
    } catch (e) { setTransMem(null); }
  };

  const loadBudget = async () => {
    try {
      const res = await axios.get(`/api/projects/${projectId}/budget`, { headers });
      setBudget(res.data);
      if (res.data.configured) {
        setBudgetForm({
          budget_usd: res.data.budget_usd,
          alert_threshold: Math.round(res.data.alert_threshold * 100),
          period_type: res.data.period_type
        });
      }
    } catch (e) { if (e.response?.status === 403) setAccessError(e.response?.data?.message || 'Accès insuffisant à ce projet.'); setBudget(null); }
  };

  const handleSaveBudget = async () => {
    setBudgetSaving(true);
    setBudgetMsg('');
    try {
      const res = await axios.put(`/api/projects/${projectId}/budget`, {
        budget_usd: parseFloat(budgetForm.budget_usd) || 5,
        alert_threshold: (parseInt(budgetForm.alert_threshold) || 80) / 100,
        period_type: budgetForm.period_type
      }, { headers });
      setBudget(res.data);
      setBudgetMsg('Sauvegardé');
      setTimeout(() => setBudgetMsg(''), 2500);
    } catch (e) {
      setBudgetMsg(e.response?.data?.message || 'Erreur');
    } finally {
      setBudgetSaving(false);
    }
  };

  const handleResetBudget = async () => {
    if (!await confirmModal('Remettre les dépenses à zéro ? L\'historique des coûts n\'est pas affecté.', { danger: true, confirmText: 'Remettre à zéro' })) return;
    try {
      await axios.post(`/api/projects/${projectId}/budget/reset`, {}, { headers });
      loadBudget();
      toast.success('Dépenses remises à zéro');
    } catch (e) { toast.error(e.response?.data?.message || e.response?.data?.error || 'Erreur reset budget'); }
  };

  // Phase B2 — Bases d'expertise attachées au projet
  const loadExpertiseAttachments = async () => {
    try {
      const res = await axios.get(`/api/expertise-bases/project/${projectId}/attachments`, { headers });
      setExpertiseAttachments(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setExpertiseAttachments([]); }
  };

  const loadAccessibleBases = async () => {
    try {
      const res = await axios.get('/api/expertise-bases', { headers });
      setAccessibleBases(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setAccessibleBases([]); }
  };

  const handleAttachBase = async () => {
    if (!attachSelectedBaseId) return;
    setExpertiseSaving(true);
    try {
      await axios.post(`/api/expertise-bases/${attachSelectedBaseId}/attach`, {
        project_id: projectId,
        injection_mode: attachMode,
      }, { headers });
      toast.success('Base attachée');
      setAttachModalOpen(false);
      setAttachSelectedBaseId('');
      setAttachMode('hybrid');
      loadExpertiseAttachments();
    } catch (e) {
      toast.error('Erreur attachement : ' + (e.response?.data?.error || e.message));
    } finally {
      setExpertiseSaving(false);
    }
  };

  const handleChangeInjectionMode = async (baseId, newMode) => {
    setExpertiseSaving(true);
    try {
      // UPSERT côté backend : re-appeler attach avec le nouveau mode
      await axios.post(`/api/expertise-bases/${baseId}/attach`, {
        project_id: projectId,
        injection_mode: newMode,
      }, { headers });
      toast.success('Mode d\'injection mis à jour');
      loadExpertiseAttachments();
    } catch (e) {
      toast.error('Erreur mise à jour : ' + (e.response?.data?.error || e.message));
    } finally {
      setExpertiseSaving(false);
    }
  };

  const handleDetachBase = async (baseId, baseName) => {
    if (!await confirmModal(`Détacher la base "${baseName}" de ce projet ? Le chat ne pourra plus la consulter.`, { confirmText: 'Détacher' })) return;
    setExpertiseSaving(true);
    try {
      await axios.delete(`/api/expertise-bases/${baseId}/attach/${projectId}`, { headers });
      toast.success('Base détachée');
      loadExpertiseAttachments();
    } catch (e) {
      toast.error('Erreur détachement : ' + (e.response?.data?.error || e.message));
    } finally {
      setExpertiseSaving(false);
    }
  };

  const loadFileStats = async () => {
    try {
      const res = await axios.get(`/api/projects/${projectId}/files?page=1&limit=500`, { headers });
      const files = res.data?.files || [];
      const totalSize = files.reduce((s, f) => s + (parseInt(f.file_size || f.size) || 0), 0);
      const types = {};
      files.forEach(f => {
        const ext = (f.original_name || '').split('.').pop().toLowerCase();
        const cat = ['pdf', 'doc', 'docx'].includes(ext) ? 'Documents' :
                    ['txt', 'md'].includes(ext) ? 'Texte' :
                    ['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(ext) ? 'Images' :
                    ['json', 'csv', 'xml'].includes(ext) ? 'Données' : 'Autres';
        types[cat] = (types[cat] || 0) + 1;
      });
      setFileStats({
        total: files.length,
        size: totalSize < 1024 ? `${totalSize} o` : totalSize < 1048576 ? `${Math.round(totalSize / 1024)} KB` : `${(totalSize / 1048576).toFixed(1)} MB`,
        types
      });
    } catch (e) { setFileStats({ total: 0, size: '0 KB', types: {} }); }

    // Session 36 — quota stockage backend (authoritative)
    try {
      const sres = await axios.get(`/api/projects/${projectId}/storage`, { headers });
      if (sres.data?.success) setStorageInfo(sres.data);
    } catch (e) { /* silencieux */ }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const tagsArray = form.tags
        ? form.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      await axios.put(`/api/projects/${projectId}`, {
        name: form.name.trim(),
        description: form.description.trim(),
        project_type: form.project_type,
        deadline: form.deadline || null,
        tags: tagsArray,
      }, { headers });
      setSaveMsg('Sauvegardé');
      if (onProjectUpdate) onProjectUpdate({ ...projectData, ...form, tags: tagsArray });
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (e) {
      setSaveMsg('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!await confirmModal('Archiver ce projet ? Il sera déplacé dans les archives.', { confirmText: 'Archiver' })) return;
    try {
      await axios.post(`/api/projects/${projectId}/archive`, {}, { headers });
      toast.success('Projet archivé');
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error('Erreur archivage: ' + (e.response?.data?.error || e.message)); }
  };

  const handleDelete = async () => {
    const name = projectData?.name || '';
    if (!await confirmModal(`Supprimer définitivement "${name}" ? Cette action est irréversible.`, { danger: true, confirmText: 'Supprimer' })) return;
    try {
      await axios.delete(`/api/projects/${projectId}`, { headers });
      toast.success('Projet supprimé');
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error('Erreur suppression: ' + (e.response?.data?.error || e.message)); }
  };

  const Section = ({ title, icon: Icon, children, color = '#1D4ED8' }) => (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
      borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px',
      // Portfolio masonry : chaque card insécable entre colonnes + espacement vertical indépendant
      breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', pageBreakInside: 'avoid',
      marginBottom: '20px'
    }}>
      <h4 style={{
        fontSize: '14px', fontWeight: '600', color: 'var(--text)',
        display: 'flex', alignItems: 'center', gap: '10px', margin: 0
      }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '8px',
          background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Icon size={16} style={{ color }} />
        </div>
        {title}
      </h4>
      {children}
    </div>
  );

  const inputStyle = {
    width: '100%', padding: '8px 12px', border: '1px solid var(--card-border)',
    borderRadius: '6px', fontSize: '13px', background: 'var(--input-bg)',
    color: 'var(--text)', outline: 'none'
  };

  const labelStyle = { fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '4px' };

  const projectTypes = [
    { value: 'development', label: 'Développement' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'team', label: 'Équipe' },
    { value: 'research', label: 'Recherche' },
    { value: 'documentation', label: 'Documentation' },
    { value: 'comm', label: 'Communication' },
    { value: 'expertise', label: 'Expertise' },
    { value: 'personal', label: 'Personnel' },
    { value: 'system', label: 'Système' },
  ];

  return (
    <div style={{
      // Portfolio masonry : CSS multi-columns, chaque card remplit l'espace vertical sans vide
      columnWidth: '340px',
      columnGap: '20px',
      columnFill: 'balance',
    }}>

      {/* Section 1 — Identité */}
      <Section title="Identité Projet" icon={FileText} color="#2563eb">
        <div>
          <div style={labelStyle}>Nom du projet</div>
          <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </div>
        <div>
          <div style={labelStyle}>Description</div>
          <textarea style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
            value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <div style={labelStyle}>Type</div>
            <select style={inputStyle} value={form.project_type}
              onChange={e => setForm(f => ({ ...f, project_type: e.target.value }))}>
              {projectTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Deadline</div>
            <input type="date" style={inputStyle} value={form.deadline}
              onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
          </div>
        </div>
        <div>
          <div style={labelStyle}>Tags (séparés par des virgules)</div>
          <input style={inputStyle} value={form.tags} placeholder="IA, architecture, MVP..."
            onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
        </div>
        <button onClick={handleSave} disabled={saving} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          padding: '10px', background: saving ? 'var(--card-border)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
          color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px',
          fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer'
        }}>
          <Save size={14} />
          {saving ? 'Sauvegarde...' : saveMsg || 'Sauvegarder'}
        </button>
      </Section>

      {/* Section 2 — Mémoire Persistante */}
      <Section title="Mémoire Persistante" icon={Brain} color="#1D4ED8">
        {accessError && (
          <div style={{ padding: 12, marginBottom: 12, borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', fontSize: 13 }}>
            {accessError}
          </div>
        )}
        {memory ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              {[
                { label: 'Conversations', value: memory.total_conversations || 0, color: '#1D4ED8' },
                { label: 'Thèmes', value: memory.themes?.length || 0, color: '#8b5cf6' },
                { label: 'Décisions', value: memory.decisions?.length || 0, color: '#2563eb' }
              ].map((s, i) => (
                <div key={i} style={{
                  textAlign: 'center', padding: '10px', background: 'var(--input-bg)',
                  borderRadius: '8px', border: '1px solid var(--card-border)'
                }}>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Barre santé mémoire */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                <span>Profondeur mémoire</span>
                <span>{memory.memory_depth || 'N/A'}</span>
              </div>
              <div style={{ height: '6px', background: 'var(--input-bg)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min((memory.total_conversations || 0) * 2, 100)}%`,
                  height: '100%', borderRadius: '3px',
                  background: 'linear-gradient(90deg, #1D4ED8, #f59e0b)'
                }} />
              </div>
            </div>

            {memory.open_questions?.length > 0 && (
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: '600' }}>{memory.open_questions.length} questions ouvertes</span>
              </div>
            )}

            {/* Meta Projets Memory */}
            {transMem && (
              <div style={{
                padding: '10px 12px', background: 'rgba(139, 92, 246, 0.06)',
                border: '1px solid rgba(139, 92, 246, 0.15)', borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#8b5cf6', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Database size={12} />
                  Meta Projets Memory
                </div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  <span><strong style={{ color: '#8b5cf6' }}>{transMem.linked_projects?.length || 0}</strong> projets liés</span>
                  <span><strong style={{ color: '#8b5cf6' }}>{transMem.insights_count || 0}</strong> insights</span>
                </div>
                {(transMem.linked_projects?.length || 0) === 0 && (
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Liez des projets dans l'onglet Meta Projets Memory
                  </div>
                )}
              </div>
            )}

            {memory.summary && (
              <div>
                <button onClick={() => setShowSummary(!showSummary)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '12px', color: '#1D4ED8', display: 'flex', alignItems: 'center', gap: '4px', padding: 0
                }}>
                  {showSummary ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {showSummary ? 'Masquer le résumé' : 'Voir le résumé complet'}
                </button>
                {showSummary && (
                  <div style={{
                    marginTop: '8px', padding: '12px', background: 'var(--input-bg)',
                    borderRadius: '8px', fontSize: '12px', color: 'var(--text-secondary)',
                    lineHeight: '1.6', maxHeight: '200px', overflowY: 'auto'
                  }}>
                    {memory.summary}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-secondary)', fontSize: '12px' }}>
            Chargement...
          </div>
        )}
      </Section>

      {/* Section 2b — Sessions hors mémoire (S50) */}
      <Section title={`Sessions hors mémoire${ephemeralSessions.total > 0 ? ` · ${ephemeralSessions.total}` : ''}`} icon={Lock} color="#f59e0b">
        {ephemeralSessions.total === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
            Aucune session hors mémoire dans ce projet. Lorsque vous cliquez sur le cadenas d'une conversation, elle apparaît ici et n'est plus indexée en mémoire persistante. Réactivable à tout moment.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {ephemeralSessions.sessions.map(s => (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '12px', padding: '10px 12px',
                background: 'rgba(245, 158, 11, 0.06)',
                border: '1px solid rgba(245, 158, 11, 0.22)',
                borderRadius: '8px'
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                    <Lock size={12} style={{ color: '#f59e0b', flexShrink: 0 }} />
                    <span style={{
                      fontSize: '13px', fontWeight: '600', color: 'var(--text)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                      {s.title || `Session #${s.id}`}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <span>{s.messages_count || 0} msg</span>
                    {s.memory_scope_changed_at && (
                      <span>
                        Suspendue le {new Date(s.memory_scope_changed_at).toLocaleDateString('fr-FR', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                      </span>
                    )}
                    {(s.changed_by_full_name || s.changed_by_username) && (
                      <span>par {s.changed_by_full_name || s.changed_by_username}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => reactivateSession(s.id)}
                  disabled={reactivatingId === s.id}
                  title="Réactiver la mémoire (réindex rétroactif, hors messages supprimés)"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 10px',
                    background: reactivatingId === s.id ? 'var(--input-bg)' : 'rgba(29, 78, 216, 0.08)',
                    border: '1px solid rgba(29, 78, 216, 0.25)',
                    borderRadius: '6px',
                    color: '#1D4ED8', fontSize: '11px', fontWeight: '600',
                    cursor: reactivatingId === s.id ? 'wait' : 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Unlock size={12} />
                  {reactivatingId === s.id ? 'Réactivation…' : 'Réactiver'}
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Section 3 — Stockage & Fichiers */}
      <Section title="Stockage & Fichiers" icon={HardDrive} color="#059669">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div style={{ textAlign: 'center', padding: '10px', background: 'var(--input-bg)', borderRadius: '8px' }}>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#059669' }}>{fileStats.total}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Fichiers</div>
          </div>
          <div style={{ textAlign: 'center', padding: '10px', background: 'var(--input-bg)', borderRadius: '8px' }}>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#2563eb' }}>{storageInfo?.used_human || fileStats.size}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Espace utilisé</div>
          </div>
        </div>

        {/* Session 36 — Barre de progression quota stockage */}
        {storageInfo && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
              <span>Quota projet</span>
              <span style={{
                fontWeight: '600',
                color: storageInfo.percent >= 100 ? '#ef4444' : storageInfo.percent >= 80 ? '#f59e0b' : '#059669'
              }}>
                {storageInfo.used_human} / {storageInfo.quota_human} ({storageInfo.percent}%)
              </span>
            </div>
            <div style={{ height: '8px', background: 'var(--input-bg)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(storageInfo.percent, 100)}%`,
                height: '100%', borderRadius: '4px', transition: 'width 0.5s ease',
                background: storageInfo.percent >= 100
                  ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                  : storageInfo.percent >= 80
                    ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                    : 'linear-gradient(90deg, #059669, #047857)'
              }} />
            </div>
            {storageInfo.percent >= 80 && (
              <div style={{
                marginTop: '6px', fontSize: '11px',
                color: storageInfo.percent >= 100 ? '#ef4444' : '#f59e0b',
                display: 'flex', alignItems: 'center', gap: '4px'
              }}>
                <AlertTriangle size={12} />
                {storageInfo.percent >= 100
                  ? 'Quota atteint — les uploads sont bloqués'
                  : `Attention : ${storageInfo.remaining_human} disponibles`}
              </div>
            )}
          </div>
        )}

        {Object.keys(fileStats.types).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {Object.entries(fileStats.types).map(([type, count]) => (
              <span key={type} style={{
                fontSize: '10px', padding: '3px 8px', borderRadius: '4px',
                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                color: 'var(--text-secondary)'
              }}>
                {type}: {count}
              </span>
            ))}
          </div>
        )}

        <button onClick={() => onTabSwitch && onTabSwitch('files')} style={{
          background: 'none', border: '1px solid var(--card-border)',
          borderRadius: '6px', padding: '8px', fontSize: '12px',
          color: '#059669', cursor: 'pointer', textAlign: 'center'
        }}>
          Voir tous les fichiers
        </button>
      </Section>

      {/* Section 4 — Équipe */}
      <Section title="Équipe Projet" icon={Users} color="#f59e0b">
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '10px 12px', background: 'var(--input-bg)', borderRadius: '8px'
        }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #1D4ED8, #f59e0b)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '13px', fontWeight: '600'
          }}>
            {(projectData?.creator_id || 'U')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text)' }}>
              {projectData?.creator_id || user?.username || 'Créateur'}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Créateur du projet</div>
          </div>
          <span style={{
            marginLeft: 'auto', fontSize: '9px', padding: '2px 6px',
            borderRadius: '3px', background: '#f59e0b20', color: '#f59e0b', fontWeight: '600'
          }}>
            CRÉATEUR
          </span>
        </div>
        <button onClick={() => onTabSwitch && onTabSwitch('team')} style={{
          marginTop: '8px', width: '100%', padding: '10px', borderRadius: '8px',
          background: 'var(--input-bg)', border: '1px solid var(--card-border)',
          color: '#3b82f6', fontSize: '12px', fontWeight: '500', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          transition: 'all 0.15s'
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = 'rgba(59,130,246,0.06)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.background = 'var(--input-bg)'; }}
        >
          <Users size={13} /> Gérer l'équipe →
        </button>
      </Section>

      {/* Section 5 — Budget & Coûts */}
      <Section title="Budget & Coûts" icon={DollarSign} color="#10b981">
        {accessError && (
          <div style={{ padding: 12, marginBottom: 12, borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', fontSize: 13 }}>
            {accessError}
          </div>
        )}
        {budget ? (
          <>
            {/* Barre de progression budget */}
            {budget.configured && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  <span>Dépensé</span>
                  <span style={{
                    fontWeight: '600',
                    color: (budget.percent || 0) >= 100 ? '#ef4444' : (budget.percent || 0) >= 80 ? '#f59e0b' : '#10b981'
                  }}>
                    ${budget.spent_usd?.toFixed(4)} / ${budget.budget_usd?.toFixed(2)} ({budget.percent || 0}%)
                  </span>
                </div>
                <div style={{ height: '8px', background: 'var(--input-bg)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(budget.percent || 0, 100)}%`,
                    height: '100%', borderRadius: '4px', transition: 'width 0.5s ease',
                    background: (budget.percent || 0) >= 100
                      ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                      : (budget.percent || 0) >= 80
                        ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                        : 'linear-gradient(90deg, #10b981, #059669)'
                  }} />
                </div>
                {budget.percent >= 80 && budget.percent < 100 && (
                  <div style={{
                    marginTop: '6px', padding: '6px 10px', borderRadius: '6px',
                    background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)',
                    fontSize: '11px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    <AlertTriangle size={12} />
                    Seuil d'alerte atteint ({budget.percent}%)
                  </div>
                )}
                {budget.percent >= 100 && (
                  <div style={{
                    marginTop: '6px', padding: '6px 10px', borderRadius: '6px',
                    background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)',
                    fontSize: '11px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    <AlertTriangle size={12} />
                    Budget dépassé — chat projet bloqué
                  </div>
                )}
              </div>
            )}

            {/* Formulaire budget */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <div style={labelStyle}>Budget max (USD)</div>
                <input type="number" step="0.5" min="0" style={inputStyle}
                  value={budgetForm.budget_usd}
                  onChange={e => setBudgetForm(f => ({ ...f, budget_usd: e.target.value }))} />
              </div>
              <div>
                <div style={labelStyle}>Seuil alerte (%)</div>
                <input type="number" min="50" max="100" style={inputStyle}
                  value={budgetForm.alert_threshold}
                  onChange={e => setBudgetForm(f => ({ ...f, alert_threshold: e.target.value }))} />
              </div>
            </div>
            <div>
              <div style={labelStyle}>Période</div>
              <select style={inputStyle} value={budgetForm.period_type}
                onChange={e => setBudgetForm(f => ({ ...f, period_type: e.target.value }))}>
                <option value="monthly">Mensuel</option>
                <option value="weekly">Hebdomadaire</option>
                <option value="unlimited">Illimité</option>
              </select>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleSaveBudget} disabled={budgetSaving} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                padding: '10px', background: budgetSaving ? 'var(--card-border)' : 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px',
                fontWeight: '600', cursor: budgetSaving ? 'not-allowed' : 'pointer'
              }}>
                <Save size={14} />
                {budgetSaving ? 'Sauvegarde...' : budgetMsg || 'Configurer budget'}
              </button>
              {budget.configured && (
                <button onClick={handleResetBudget} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                  padding: '10px 14px', background: 'transparent', color: 'var(--text-secondary)',
                  border: '1px solid var(--card-border)', borderRadius: '8px', fontSize: '12px',
                  cursor: 'pointer'
                }}>
                  <RotateCcw size={13} />
                  RAZ
                </button>
              )}
            </div>

            {!budget.configured && (
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                Aucun budget configuré — pas de limite sur le chat projet
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-secondary)', fontSize: '12px' }}>
            Chargement...
          </div>
        )}
      </Section>

      {/* Section 5b — Consolidations mémoire (S48 + S50 auto L1) */}
      <Section title="Consolidations mémoire" icon={GitCommit} color="#3b82f6">
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.55 }}>
          Chaque consolidation remplace une conversation brute par une synthèse structurée dans la mémoire du projet.
          Les messages d'origine restent récupérables pendant 90 jours.
        </div>
        <ConsolidationHistory projectId={projectId} />

        {/* S50 Phase 3.4 — Suggestions auto Niveau 1 */}
        <div style={{
          marginTop: 14, paddingTop: 14,
          borderTop: '1px dashed var(--card-border)',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Suggestion automatique</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, maxWidth: 360 }}>
                ITRIIA vous propose de consolider une conversation quand un des seuils ci-dessous est atteint.
                Aucune consolidation n'est générée automatiquement — vous gardez la décision finale.
              </div>
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={consSettingsForm.auto_suggest}
                onChange={(e) => setConsSettingsForm(f => ({ ...f, auto_suggest: e.target.checked }))}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12, color: 'var(--text)' }}>
                {consSettingsForm.auto_suggest ? 'Actif' : 'Désactivé'}
              </span>
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, opacity: consSettingsForm.auto_suggest ? 1 : 0.5 }}>
            {[
              { key: 'threshold_msgs', label: 'Messages', suffix: 'msg', min: 5, max: 500, step: 1 },
              { key: 'threshold_idle_days', label: 'Inactivité', suffix: 'j', min: 1, max: 90, step: 1 },
              { key: 'threshold_cost_usd', label: 'Coût cumulé', suffix: 'USD', min: 0.1, max: 1000, step: 0.5 },
            ].map(f => (
              <div key={f.key} style={{
                padding: 10, background: 'var(--input-bg)',
                border: '1px solid var(--card-border)', borderRadius: 8,
              }}>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 4 }}>{f.label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <input
                    type="number"
                    min={f.min} max={f.max} step={f.step}
                    value={consSettingsForm[f.key]}
                    disabled={!consSettingsForm.auto_suggest}
                    onChange={(e) => setConsSettingsForm(x => ({
                      ...x,
                      [f.key]: f.key === 'threshold_cost_usd' ? parseFloat(e.target.value) : parseInt(e.target.value) || 0,
                    }))}
                    style={{
                      width: '60%', fontSize: 16, fontWeight: 700,
                      background: 'transparent', border: 'none', outline: 'none',
                      color: '#3b82f6', padding: 0,
                    }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{f.suffix}</span>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={saveConsSettings}
            disabled={consSettingsSaving}
            style={{
              alignSelf: 'flex-start',
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 8,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none', color: 'white',
              fontSize: 12, fontWeight: 600,
              cursor: consSettingsSaving ? 'wait' : 'pointer',
            }}
          >
            <Save size={12} />
            {consSettingsSaving ? 'Sauvegarde…' : 'Enregistrer les préférences'}
          </button>
        </div>
      </Section>

      {/* Section 6 — Bases d'expertise attachées (Phase B2) */}
      <Section title="Bases d'expertise attachées" icon={BookMarked} color="#8b5cf6">
        {expertiseAttachments.length === 0 ? (
          <div style={{
            padding: '14px', background: 'var(--input-bg)', borderRadius: '8px',
            fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center',
            lineHeight: '1.6'
          }}>
            Aucune base d'expertise attachée à ce projet.
            <br />
            L'IA répond uniquement à partir de la mémoire projet standard.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {expertiseAttachments.map(att => (
              <div key={att.id} style={{
                padding: '12px', background: 'var(--input-bg)',
                border: '1px solid var(--card-border)', borderRadius: '10px',
                display: 'flex', flexDirection: 'column', gap: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {att.name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {att.template?.name || att.slug}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDetachBase(att.id, att.name)}
                    disabled={expertiseSaving}
                    title="Détacher"
                    style={{
                      background: 'transparent', border: '1px solid var(--card-border)',
                      borderRadius: '6px', padding: '6px', color: '#ef4444',
                      cursor: expertiseSaving ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Mode d'injection
                  </div>
                  <select
                    value={att.injection_mode || 'hybrid'}
                    disabled={expertiseSaving}
                    onChange={e => handleChangeInjectionMode(att.id, e.target.value)}
                    style={{
                      ...inputStyle, fontSize: '12px', padding: '6px 10px',
                      cursor: expertiseSaving ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="always">Toujours (2 extraits injectés)</option>
                    <option value="hybrid">Hybride (1 extrait + outil)</option>
                    <option value="tool_only">Outil seul (aucune injection auto)</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => { setAttachSelectedBaseId(''); setAttachMode('hybrid'); setAttachModalOpen(true); }}
          disabled={accessibleBases.length === 0}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '10px', background: accessibleBases.length === 0 ? 'var(--card-border)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
            color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px',
            fontWeight: '600', cursor: accessibleBases.length === 0 ? 'not-allowed' : 'pointer',
            opacity: accessibleBases.length === 0 ? 0.5 : 1
          }}
        >
          <Plus size={14} />
          {accessibleBases.length === 0 ? 'Aucune base disponible' : 'Attacher une base'}
        </button>

        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
          Les bases attachées enrichissent la mémoire du chat projet avec des extraits factuels.
          Aucune directive de ton ou de posture n'est imposée — le porteur de projet reste maître de la voix de son IA.
        </div>

        {/* Modale d'attachement */}
        {attachModalOpen && (
          <div
            onClick={() => !expertiseSaving && setAttachModalOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, backdropFilter: 'blur(4px)'
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '440px',
                display: 'flex', flexDirection: 'column', gap: '16px'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <BookMarked size={18} style={{ color: '#8b5cf6' }} />
                Attacher une base d'expertise
              </h3>
              <div>
                <div style={labelStyle}>Base à attacher</div>
                <select
                  value={attachSelectedBaseId}
                  onChange={e => setAttachSelectedBaseId(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">— Choisir une base —</option>
                  {accessibleBases
                    .filter(b => !expertiseAttachments.some(att => att.id === b.id))
                    .map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                </select>
                {accessibleBases.filter(b => !expertiseAttachments.some(att => att.id === b.id)).length === 0 && (
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Toutes les bases accessibles sont déjà attachées.
                  </div>
                )}
              </div>
              <div>
                <div style={labelStyle}>Mode d'injection</div>
                <select value={attachMode} onChange={e => setAttachMode(e.target.value)} style={inputStyle}>
                  <option value="always">Toujours (2 extraits injectés)</option>
                  <option value="hybrid">Hybride (1 extrait + outil)</option>
                  <option value="tool_only">Outil seul (aucune injection auto)</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setAttachModalOpen(false)}
                  disabled={expertiseSaving}
                  style={{
                    padding: '10px 16px', background: 'transparent',
                    border: '1px solid var(--card-border)', borderRadius: '8px',
                    fontSize: '12px', color: 'var(--text)', cursor: 'pointer'
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleAttachBase}
                  disabled={!attachSelectedBaseId || expertiseSaving}
                  style={{
                    padding: '10px 16px', background: (!attachSelectedBaseId || expertiseSaving) ? 'var(--card-border)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    color: 'white', border: 'none', borderRadius: '8px',
                    fontSize: '12px', fontWeight: '600',
                    cursor: (!attachSelectedBaseId || expertiseSaving) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {expertiseSaving ? 'Attachement...' : 'Attacher'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Section>

      {/* Section COM/Écrivain — Atelier d'écriture (S56) */}
      <WritingSection projectId={projectId} projectData={projectData} onBookCreated={onProjectUpdate} onTabSwitch={onTabSwitch} />

      {/* Section 6b — Historique des organisations IA (S59 P3) */}
      <Section title="Organisations de fichiers IA" icon={FolderTree} color="#8b5cf6">
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.55 }}>
          Chaque organisation appliquée est journalisée. Tu peux annuler dans les 72h qui suivent — les fichiers
          retrouvent leur emplacement d'origine et les dossiers créés vides sont supprimés.
        </div>

        {loadingOrganizations && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 12, padding: '12px 0' }}>
            <Loader size={14} className="animate-spin" />
            Chargement de l'historique…
          </div>
        )}

        {!loadingOrganizations && organizationsHistory.length === 0 && (
          <div style={{
            padding: '16px 12px', borderRadius: 8,
            background: 'rgba(139,92,246,0.04)',
            border: '1px dashed rgba(139,92,246,0.2)',
            fontSize: 12, color: 'var(--text-muted)', textAlign: 'center',
          }}>
            Aucune organisation IA appliquée pour ce projet.
          </div>
        )}

        {!loadingOrganizations && organizationsHistory.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {organizationsHistory.map(entry => {
              const canRollback = !!entry.can_rollback;
              const isRolledBack = !!entry.rolled_back_at;
              const isExpired = !canRollback && !isRolledBack;
              const statusMeta = isRolledBack
                ? { label: 'Annulée', color: '#6b7280', bg: 'rgba(107,114,128,0.15)' }
                : isExpired
                  ? { label: 'Expirée', color: '#6b7280', bg: 'rgba(107,114,128,0.1)' }
                  : { label: 'Actif', color: '#10b981', bg: 'rgba(16,185,129,0.12)' };
              const isRollingBack = rollbackInProgressId === entry.id;

              return (
                <div
                  key={entry.id}
                  style={{
                    padding: '12px 14px', borderRadius: 8,
                    background: isRolledBack ? 'rgba(107,114,128,0.04)' : 'rgba(139,92,246,0.03)',
                    border: '1px solid var(--card-border)',
                    opacity: isRolledBack ? 0.7 : 1,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 600 }}>
                          {formatRelativeTime(entry.applied_at)}
                        </span>
                        <span style={{
                          padding: '2px 8px', borderRadius: 10,
                          background: statusMeta.bg, color: statusMeta.color,
                          fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4,
                        }}>{statusMeta.label}</span>
                        {entry.model_used && (
                          <span style={{
                            padding: '2px 7px', borderRadius: 10,
                            background: 'rgba(139,92,246,0.12)', color: '#a78bfa',
                            fontSize: 10, fontWeight: 500,
                          }}>{entry.model_used.split('/').pop()}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                        {entry.files_moved} fichier{entry.files_moved > 1 ? 's' : ''} déplacé{entry.files_moved > 1 ? 's' : ''}
                        {entry.folders_created > 0 && ` · ${entry.folders_created} dossier${entry.folders_created > 1 ? 's' : ''} créé${entry.folders_created > 1 ? 's' : ''}`}
                        {entry.duplicates_deleted > 0 && ` · ${entry.duplicates_deleted} doublon${entry.duplicates_deleted > 1 ? 's' : ''} supprimé${entry.duplicates_deleted > 1 ? 's' : ''}`}
                        {entry.cost_usd != null && ` · $${Number(entry.cost_usd).toFixed(4)}`}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3 }}>
                        {formatRollbackCountdown(entry.rollback_until, entry.rolled_back_at)}
                      </div>
                    </div>

                    {canRollback && !isRolledBack && (
                      <button
                        onClick={() => handleOrganizationRollback(entry.id, entry)}
                        disabled={isRollingBack}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '6px 12px', borderRadius: 6,
                          background: 'rgba(245,158,11,0.1)',
                          border: '1px solid rgba(245,158,11,0.35)',
                          color: '#f59e0b', fontSize: 11.5, fontWeight: 600,
                          cursor: isRollingBack ? 'not-allowed' : 'pointer',
                          opacity: isRollingBack ? 0.6 : 1,
                          flexShrink: 0,
                        }}
                      >
                        {isRollingBack
                          ? <><Loader size={11} className="animate-spin" /> Annulation…</>
                          : <><RotateCcw size={11} /> Annuler</>}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Section 7 — Zone de danger */}
      <Section title="Zone de danger" icon={AlertTriangle} color="#3b82f6">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button onClick={handleArchive} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '10px', background: 'transparent', color: '#3b82f6',
            border: '1px solid #3b82f6', borderRadius: '8px', fontSize: '12px',
            fontWeight: '600', cursor: 'pointer'
          }}>
            <Archive size={14} />
            Archiver ce projet
          </button>
          <button onClick={handleDelete} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '10px', background: 'linear-gradient(135deg, #3b82f6, #1D4ED8)',
            color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px',
            fontWeight: '600', cursor: 'pointer'
          }}>
            <Trash2 size={14} />
            Supprimer définitivement
          </button>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textAlign: 'center' }}>
            La suppression est irréversible. Tapez le nom du projet pour confirmer.
          </div>
        </div>
      </Section>
    </div>
  );
};

// ─── S56 — Section "Atelier d'écriture" (COM/Écrivain Foundation) ────
const WritingSection = ({ projectId, projectData, onBookCreated, onTabSwitch }) => {
  const hasBook = !!projectData?.book_id;
  const [featureFlags, setFeatureFlags] = useState(() => window.__itriiaFeatureFlags || null);
  useEffect(() => {
    const onReady = (e) => setFeatureFlags(e.detail || null);
    window.addEventListener('itriia-features-ready', onReady);
    return () => window.removeEventListener('itriia-features-ready', onReady);
  }, []);
  const comWriterEnabled = !featureFlags || featureFlags.com_writer !== false;
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '', subtitle: '', genre: 'essai',
    style_benchmark: 'Harari/Sapiens — prose dense continue, zéro listes à puces, ancrages anthropologiques.',
    writing_guidelines: '',
  });
  const docTerms = getDocTerms(form.genre);
  const [pending, setPending] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  // P3.3 — toggle "Ouvrir l'Atelier à l'entrée du projet"
  const [openAtEntry, setOpenAtEntry] = useState(false);
  const [togglePending, setTogglePending] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!hasBook || !projectData?.book_id) { setOpenAtEntry(false); return; }
    getBook(projectData.book_id).then(res => {
      if (!cancelled && res?.book) setOpenAtEntry(!!res.book.open_at_entry);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [hasBook, projectData?.book_id]);

  const handleToggleOpenAtEntry = async () => {
    if (!projectData?.book_id || togglePending) return;
    const next = !openAtEntry;
    setTogglePending(true);
    setOpenAtEntry(next); // optimistic
    try {
      await updateBook(projectData.book_id, { open_at_entry: next });
      toast.success(next
        ? 'Atelier ouvert par défaut à l\'entrée du projet.'
        : 'Chat Projet ouvert par défaut à l\'entrée du projet.');
    } catch (err) {
      setOpenAtEntry(!next); // rollback
      toast.error('Impossible de mettre à jour ce réglage.');
    } finally {
      setTogglePending(false);
    }
  };

  if (!comWriterEnabled) return null;

  const handleCreate = async () => {
    if (!form.title.trim()) {
      setErrMsg('Titre requis.');
      return;
    }
    setPending(true);
    setErrMsg('');
    try {
      const result = await createBook({
        project_id: projectId,
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || null,
        genre: form.genre,
        style_benchmark: form.style_benchmark.trim() || null,
        writing_guidelines: form.writing_guidelines.trim() || null,
        initialize_empty: true,
      });
      if (result?.book) {
        toast.success('Livre créé — ouverture de l\'atelier');
        if (onBookCreated) onBookCreated({ book_id: result.book.id });
        setTimeout(() => {
          if (onTabSwitch) onTabSwitch('book');
        }, 300);
      }
    } catch (err) {
      setErrMsg(err.detail?.message || err.message || 'Erreur création livre');
    } finally {
      setPending(false);
    }
  };

  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
      borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px',
      breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', pageBreakInside: 'avoid',
      marginBottom: '20px'
    }}>
      <h4 style={{
        fontSize: '14px', fontWeight: '600', color: 'var(--text)',
        display: 'flex', alignItems: 'center', gap: '8px', margin: 0,
      }}>
        <BookOpen size={14} style={{ color: '#8b5cf6' }} />
        Atelier d'écriture
      </h4>

      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        Structure un document (livre, thèse, rapport, article, doc technique…) en {docTerms.unitPlural.toLowerCase()}
        {' '}avec versioning intelligent, posture méthodologique active dans le chat projet, révision
        IA assistée, comparaison de versions, import intelligent de fichiers. Le module s'adapte au format
        que tu choisis.
      </div>

      {hasBook ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, borderRadius: 8, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.18)' }}>
            <BookOpen size={16} style={{ color: '#8b5cf6', flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>Document attaché</div>
              <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>La posture COM/Écrivain est active dans le chat projet.</div>
            </div>
            <button onClick={() => onTabSwitch?.('book')} style={{
              padding: '6px 14px', borderRadius: 6,
              background: 'rgba(139,92,246,0.15)',
              border: '1px solid rgba(139,92,246,0.3)',
              color: '#a78bfa', fontSize: 12, cursor: 'pointer',
            }}>Ouvrir l'atelier</button>
          </div>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
            borderRadius: 8, background: 'var(--card-bg-secondary, rgba(255,255,255,0.02))',
            border: '1px solid var(--card-border)', cursor: togglePending ? 'wait' : 'pointer',
            opacity: togglePending ? 0.7 : 1,
          }}>
            <input
              type="checkbox"
              checked={openAtEntry}
              disabled={togglePending}
              onChange={handleToggleOpenAtEntry}
              style={{ width: 16, height: 16, accentColor: '#8b5cf6', cursor: 'inherit' }}
            />
            <div style={{ flex: 1, fontSize: 12, lineHeight: 1.45 }}>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                Ouvrir l'Atelier directement à l'entrée du projet
              </div>
              <div style={{ color: 'var(--text-secondary)', marginTop: 2, fontSize: 11 }}>
                Au lieu du Chat Projet par défaut. Pratique si ce projet est avant tout un chantier d'écriture.
              </div>
            </div>
          </label>
        </>
      ) : creating ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>
            Format du document (détermine le vocabulaire : chapitres, sections, parties…)
          </div>
          <select
            value={form.genre}
            onChange={e => setForm(f => ({ ...f, genre: e.target.value }))}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 13 }}
          >
            {listGenres().map(g => (
              <option key={g.id} value={g.id}>{g.label}</option>
            ))}
          </select>
          <input
            placeholder={`Titre ${docTerms.docArticle === 'une' ? 'de la' : 'du'} ${docTerms.docLabel.toLowerCase()}`}
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 13 }}
          />
          <input
            placeholder="Sous-titre (optionnel)"
            value={form.subtitle}
            onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 13 }}
          />
          <textarea
            placeholder="Style de référence (benchmark)"
            value={form.style_benchmark}
            onChange={e => setForm(f => ({ ...f, style_benchmark: e.target.value }))}
            rows={2}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 12, resize: 'vertical' }}
          />
          <textarea
            placeholder="Directives spécifiques à ce livre (invariants éditoriaux supplémentaires — optionnel)"
            value={form.writing_guidelines}
            onChange={e => setForm(f => ({ ...f, writing_guidelines: e.target.value }))}
            rows={3}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: 12, resize: 'vertical' }}
          />
          {errMsg && <div style={{ fontSize: 11, color: '#ef4444' }}>{errMsg}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleCreate} disabled={pending || !form.title.trim()} style={{
              flex: 1, padding: '8px 16px', borderRadius: 6,
              background: 'linear-gradient(180deg, rgba(139,92,246,0.25), rgba(139,92,246,0.15))',
              border: '1px solid rgba(139,92,246,0.4)',
              color: '#c4b5fd', fontSize: 12, fontWeight: 500, cursor: 'pointer',
              opacity: pending ? 0.6 : 1,
            }}>
              {pending ? 'Création…' : `Créer ${docTerms.docArticle} ${docTerms.docLabel.toLowerCase()}`}
            </button>
            <button onClick={() => { setCreating(false); setErrMsg(''); }} style={{
              padding: '8px 16px', borderRadius: 6,
              background: 'transparent', border: '1px solid var(--card-border)',
              color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
            }}>Annuler</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setCreating(true)} style={{
          padding: '10px 16px', borderRadius: 8,
          background: 'rgba(139,92,246,0.08)',
          border: '1px dashed rgba(139,92,246,0.3)',
          color: '#a78bfa', fontSize: 13, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <Plus size={14} /> Créer un document (livre, thèse, rapport…) pour ce projet
        </button>
      )}
    </div>
  );
};

export default ProjectSettings;
